import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { DATA_TYPE_OPTIONS } from '../aiModelConstants';
import { emptyLabel, emptyOutputField } from '../aiModelForm';
import { isClassificationTask } from '../aiModelSchema';
import { btnSecondary, inputClass } from '../aiModelUi';
import { AiModelDataType } from '../aiModelTypes';
import { StepProps } from './stepTypes';

const OutputContractStep: React.FC<StepProps> = ({ form, isLight, errors, patch }) => {
  const updateRow = (id: string, key: string, value: string | boolean) => {
    patch({
      outputFields: form.outputFields.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    });
  };

  return (
    <div className="space-y-4">
      <p className={`text-[11px] ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
        Name each output so later workflow steps can map prediction and score fields.
      </p>
      {errors.outputFields && <p className="text-[11px] text-red-500">{errors.outputFields}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={isLight ? 'text-neutral-500' : 'text-gray-400'}>
              <th className="text-left font-medium pb-2 pr-2">Output</th>
              <th className="text-left font-medium pb-2 pr-2">Type</th>
              <th className="text-left font-medium pb-2 pr-2">Score / probability</th>
              <th className="pb-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {form.outputFields.map((row) => (
              <tr key={row.id}>
                <td className="pr-2 pb-2">
                  <input
                    value={row.name}
                    onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                    placeholder="prediction"
                    className={`${inputClass(isLight)} font-mono`}
                  />
                </td>
                <td className="pr-2 pb-2">
                  <select
                    value={row.dataType}
                    onChange={(e) => updateRow(row.id, 'dataType', e.target.value as AiModelDataType)}
                    className={inputClass(isLight)}
                  >
                    {DATA_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="pr-2 pb-2 pt-2">
                  <input
                    type="checkbox"
                    checked={row.scoreSupported}
                    onChange={(e) => updateRow(row.id, 'scoreSupported', e.target.checked)}
                  />
                </td>
                <td className="pb-2 pt-1">
                  <button
                    type="button"
                    onClick={() =>
                      patch({ outputFields: form.outputFields.filter((item) => item.id !== row.id) })
                    }
                    disabled={form.outputFields.length <= 1}
                    className={`p-1.5 rounded-md ${
                      isLight ? 'text-neutral-400 hover:text-red-500' : 'text-gray-500 hover:text-red-400'
                    } disabled:opacity-30`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={() => patch({ outputFields: [...form.outputFields, emptyOutputField()] })}
        className={btnSecondary(isLight)}
      >
        <Plus size={12} /> Add output
      </button>

      {isClassificationTask(form.task) && (
        <div>
          <p className={`text-[11px] font-medium mb-2 ${isLight ? 'text-neutral-600' : 'text-gray-400'}`}>
            Classification labels
          </p>
          <div className="space-y-2">
            {form.classificationLabels.map((row) => (
              <div key={row.id} className="grid grid-cols-[7rem_1fr_auto] gap-2">
                <input
                  value={row.value}
                  onChange={(e) =>
                    patch({
                      classificationLabels: form.classificationLabels.map((item) =>
                        item.id === row.id ? { ...item, value: e.target.value } : item
                      ),
                    })
                  }
                  placeholder="0"
                  className={`${inputClass(isLight)} font-mono`}
                />
                <input
                  value={row.label}
                  onChange={(e) =>
                    patch({
                      classificationLabels: form.classificationLabels.map((item) =>
                        item.id === row.id ? { ...item, label: e.target.value } : item
                      ),
                    })
                  }
                  placeholder="Legitimate"
                  className={inputClass(isLight)}
                />
                <button
                  type="button"
                  onClick={() =>
                    patch({
                      classificationLabels: form.classificationLabels.filter((item) => item.id !== row.id),
                    })
                  }
                  className={`p-1.5 rounded-md ${
                    isLight ? 'text-neutral-400 hover:text-red-500' : 'text-gray-500 hover:text-red-400'
                  }`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => patch({ classificationLabels: [...form.classificationLabels, emptyLabel()] })}
            className={`${btnSecondary(isLight)} mt-2`}
          >
            <Plus size={12} /> Add label
          </button>
        </div>
      )}
    </div>
  );
};

export default OutputContractStep;
